import React, { useCallback, useEffect, useMemo } from "react";
import { Sprite, Graphics, Container } from "@pixi/react";
import * as PIXI from "pixi.js";
import { useDispatch, useSelector } from "react-redux";
import { SOUND_NAME } from "../../../../sounds";
import { getNewUITexture } from "../../../../assets";
import { soundManager } from "../../../../SoundManager";
import { decrementBet } from "../../../../State/actions";
import { useButtonBrightness } from "../../../../Hooks/useButtonBrightness";
import {
  selectIsSpinning,
  selectUserInterfaceDisabled,
  selectSessionExpired,
  selectBet,
  selectBets,
  selectAutoPlayActive,
  selectAnyFeatureActive,
  selectReplaySessionActive,
} from "../../../../State/selectors";

const BonusButtonDecrementBet = ({ x = 0, y = 0, size }) => {
  const dispatch = useDispatch();

  const isSpinning = useSelector(selectIsSpinning);
  const bet = useSelector(selectBet);
  const bets = useSelector(selectBets);
  const sessionExpired = useSelector(selectSessionExpired);
  const autoPlayActive = useSelector(selectAutoPlayActive);
  const userInterfaceDisabled = useSelector(selectUserInterfaceDisabled);
  const anyFeatureActive = useSelector(selectAnyFeatureActive);
  const replaySessionActive = useSelector(selectReplaySessionActive);

  // Shared hover/press hook
  const { buttonRef, handlePointerOver, handlePointerOut, triggerPress, cleanupFilters } =
    useButtonBrightness();


  const texture = getNewUITexture("icon_minus");

  // Memoize calculations
  const currentBetIndex = useMemo(() => bets.indexOf(bet), [bets, bet]);

  const canDecreaseBet = useMemo(
    () =>
      currentBetIndex > 0 &&
      !isSpinning &&
      !sessionExpired &&
      !autoPlayActive &&
      !userInterfaceDisabled &&
      !anyFeatureActive &&
      !replaySessionActive,
    [
      currentBetIndex,
      isSpinning,
      sessionExpired,
      autoPlayActive,
      userInterfaceDisabled,
      anyFeatureActive,
      replaySessionActive,
    ]
  );

  // Memoize hitArea calculation (radius in world px — Graphics isn't scaled
  // by width/height like the Sprite is, so this matches the visual size)
  const hitRadius = useMemo(() => (size / 2) * 1.4, [size]);
  const hitArea = useMemo(() => new PIXI.Circle(0, 0, hitRadius), [hitRadius]);

  const handleClick = useCallback(() => {
    if (canDecreaseBet) {
      soundManager.play(SOUND_NAME.button_click);

      if (buttonRef.current) {
        triggerPress();
      }

      dispatch(decrementBet());
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canDecreaseBet, dispatch]);

  useEffect(() => () => cleanupFilters(), [cleanupFilters]);

  const drawHitArea = useCallback(
    (g) => {
      g.clear();
      // Nearly invisible fill (required for hit detection to work)
      g.beginFill(0x000000, 0.01);
      g.drawCircle(0, 0, hitRadius);
      g.endFill();
    },
    [hitRadius]
  );

  return (
    <Container x={x} y={y}>
      {/* Interactive circular area - handles all pointer events */}
      <Graphics
        name="bonus_button_decrement_bet_interactive_area"
        eventMode={canDecreaseBet ? "static" : "none"}
        cursor={canDecreaseBet ? "pointer" : "default"}
        hitArea={hitArea}
        onpointertap={handleClick}
        onpointerover={handlePointerOver}
        onpointerout={handlePointerOut}
        draw={drawHitArea}
      />

      {/* Visual sprite (non-interactive, just for display) */}
      <Sprite
        ref={buttonRef}
        texture={texture}
        name="bonus_button_decrement_bet"
        anchor={0.5}
        width={size}
        height={size}
        x={0}
        y={0}
        eventMode="none"
        alpha={canDecreaseBet ? 1 : 0.5}
      />
    </Container>
  );
};

export default BonusButtonDecrementBet;
